import React, { useEffect, useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { useUserSchoolClasses } from '@hooks/useUserSchoolClasses'
import { useUpdateCurrentUser } from '@hooks/mutations/useUpdateCurrentUser'
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  COLORS,
  HEADING_TYPE,
  POPUPS,
  TEXT_ALIGN,
  TEXT_SIZES,
} from '@helpers/constants'
import Popup from './Popup'
import Overlay from '@components/Overlay'
import Text from '@components/Text'
import Button from '@components/Button'
import { APP_URL } from '@helpers/variables'

const Popups = () => {
  const { currentUser } = useCurrentUser()
  const { schoolClasses } = useUserSchoolClasses()
  const schoolClass = schoolClasses?.find(sc => sc.name === 'Min klass')
  const school = schoolClass && currentUser?.schools?.find(s => s.schoolClasses.some(sc => sc.id === schoolClass.id))
  const registerLink = `${APP_URL}/registrera-elev?s=${school?.id}&c=${schoolClass?.id}`
  const { post, isPosting } = useUpdateCurrentUser()

  const [openPopups, setOpenPopups] = useState([])
  const [popupHasBeenOpened, setPopupHasBeenOpened] = useState(false)
  const [seenPopups, setSeenPopups] = useState([])
  const [currentUserFullName, setCurrentUserFullName] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)

  let navigate = useNavigate()
  const timeOutRef = useRef()

  useEffect(() => {
    // console.log('🚀 ~ currentUser', currentUser)

    if (
      currentUser &&
      currentUser.acceptedEula &&
      window.location.hash === '#/'
    ) {
      let alreadySeen = currentUser.seen_popups
        ? JSON.parse(currentUser.seen_popups)
        : []

      let popupToOpen = null

      // --- Logic start

      const {
        first_name,
        last_name,
        hasVisitedLibrary,
        hasCreatedStudents,
        hasSharedBooks,
        hasCreatedSchoolGroups,
        hasSharedGroupBooks,
        hasCreatedBooks,
        hasPaidLicense,
      } = currentUser

      if (
        !alreadySeen.includes(POPUPS.DISCOVER_LIBRARY) &&
        !hasVisitedLibrary
      ) {
        popupToOpen = POPUPS.DISCOVER_LIBRARY
      } else if (
        !alreadySeen.includes(POPUPS.CREATE_STUDENTS) &&
        !hasCreatedStudents &&
        !hasPaidLicense
      ) {
        popupToOpen = POPUPS.CREATE_STUDENTS
      } else if (!alreadySeen.includes(POPUPS.SHARE_BOOK) && !hasSharedBooks) {
        popupToOpen = POPUPS.SHARE_BOOK
      } else if (
        !alreadySeen.includes(POPUPS.CREATE_GROUP) &&
        !hasCreatedSchoolGroups
      ) {
        popupToOpen = POPUPS.CREATE_GROUP
      } else if (
        !alreadySeen.includes(POPUPS.SHARE_BOOK_WITH_GROUP) &&
        !hasSharedGroupBooks
      ) {
        popupToOpen = POPUPS.SHARE_BOOK_WITH_GROUP
      } else if (
        !alreadySeen.includes(POPUPS.CREATE_BOOK) &&
        !hasCreatedBooks
      ) {
        popupToOpen = POPUPS.CREATE_BOOK
      } else if (!alreadySeen.includes(POPUPS.TIP_COLLEAGUE)) {
        // Disable tip colleague popup for now
        // popupToOpen = POPUPS.TIP_COLLEAGUE
      }

      // --- Logic end

      if (popupToOpen && !popupHasBeenOpened) {
        // Only save tip colleague as seen for now
        // All others will be shown until user has done task
        if (popupToOpen === POPUPS.TIP_COLLEAGUE) {
          alreadySeen = [...alreadySeen, popupToOpen]
        }

        // 17 dec 2024, disabeling popups

        // setSeenPopups(alreadySeen)
        // setPopupHasBeenOpened(true) // Only once per session

        // // Wait a sec before pulling it up
        // clearTimeout(timeOutRef.current)
        // timeOutRef.current = setTimeout(() => {
        //   setOpenPopups([popupToOpen])
        // }, 2500)
      }

      setCurrentUserFullName(`${first_name} ${last_name}`)
    }
  }, [currentUser, popupHasBeenOpened, window.location.hash])

  const onCtaClick = (to) => {
    setOpenPopups([])
    postSeenPopups()

    if (to) {
      navigate(to)
    }
  }

  const onCloseClick = () => {
    setOpenPopups([])
    postSeenPopups()
  }

  const postSeenPopups = async () => {
    if (currentUser) {
      const res = await post(
        {
          seen_popups: JSON.stringify(seenPopups),
          id: currentUser.id,
        },
        false
      )

      // console.log('🚀 ~ ---- res', res)
    }
  }

  const headingProps = {
    headingLevel: HEADING_TYPE.H2,
    size: TEXT_SIZES.XLARGE,
    extraClasses: 'text-center',
  }

  return (
    <>
      <Popup
        visible={openPopups.includes(POPUPS.DISCOVER_LIBRARY)}
        ctas={[
          { label: 'Bibliotek', icon: 'Library', to: '/bibliotek/avsnitt' },
          // { label: 'Mitt lärarrum', icon: 'Classes', to: '/lararrum' },
        ]}
        onCtaClick={onCtaClick}
        onCloseClick={onCloseClick}
      >
        {/* <Heading {...headingProps}>Välkommen till Doklet!</Heading> */}
        <Text align={TEXT_ALIGN.CENTER}>
          Välkommen in och botanisera i biblioteket! Hitta böcker och avsnitt
          som passar dig och dina elever.
        </Text>
      </Popup>

      <Popup
        visible={openPopups.includes(POPUPS.CREATE_STUDENTS)}
        ctas={[
          {
            label: 'Kopiera länk',
            icon: undefined,
            to:
              'klass/' + schoolClasses?.find((sc) => sc.isPrivate)?.id ??
              schoolClasses?.[0]?.id,
          },
        ]}
        onCtaClick={() => {
          navigator.clipboard.writeText(registerLink)
          setLinkCopied(true)
        }}
        onCloseClick={onCloseClick}
      >
        <Text>
          När dina elever har skrivit in sig kan du dela böcker med dem. Ge
          denna länk till eleverna.
        </Text>

        <div className="flex gap-2 items-center">
          <div className={'inline-block mt-2 py-2 pl-4 pr-10 bg-lightLightGrey'}>
            {registerLink}
          </div>
          {linkCopied && (
            <Text extraClasses={'ml-4'} color={COLORS.LIGHT_DARK_GREY.TEXT}>
              Länk kopierad!
            </Text>
          )}
        </div>
        <Text>
          Du kan alltid hämta länken i <Link className='underline' to={'klass/' + schoolClass?.id} onClick={onCloseClick}>Min klass</Link>.
        </Text>
      </Popup>

      <Popup
        visible={openPopups.includes(POPUPS.SHARE_BOOK)}
        ctas={[
          { label: 'Bibliotek', icon: 'Library', to: '/bibliotek/bocker' },
        ]}
        onCtaClick={onCtaClick}
        onCloseClick={onCloseClick}
      >
        {/* <Heading {...headingProps}>Välkommen till Doklet!</Heading> */}
        <Text align={TEXT_ALIGN.CENTER}>
          Kom igång på riktigt! Dela en bok med dina elever.
        </Text>
      </Popup>

      <Popup
        visible={openPopups.includes(POPUPS.CREATE_GROUP)}
        ctas={[{ label: 'Mitt lärarrum', icon: 'Classes', to: '/lararrum' }]}
        onCtaClick={onCtaClick}
        onCloseClick={onCloseClick}
      >
        {/* <Heading {...headingProps}>Välkommen till Doklet!</Heading> */}
        <Text align={TEXT_ALIGN.CENTER}>
          Ge eleverna böcker som passar dem. Dela in eleverna i grupper!
        </Text>
      </Popup>

      <Popup
        visible={openPopups.includes(POPUPS.SHARE_BOOK_WITH_GROUP)}
        ctas={[
          { label: 'Bibliotek', icon: 'Library', to: '/bibliotek/bocker' },
        ]}
        onCtaClick={onCtaClick}
        onCloseClick={onCloseClick}
      >
        {/* <Heading {...headingProps}>Välkommen till Doklet!</Heading> */}
        <Text align={TEXT_ALIGN.CENTER}>
          Utmana dina elever på rätt nivå! Dela böcker med eleverna i dina
          grupper.
        </Text>
      </Popup>

      <Popup
        visible={openPopups.includes(POPUPS.CREATE_BOOK)}
        ctas={[
          { label: 'Bibliotek', icon: 'Library', to: '/bibliotek/avsnitt' },
        ]}
        onCtaClick={onCtaClick}
        onCloseClick={onCloseClick}
      >
        {/* <Heading {...headingProps}>Välkommen till Doklet!</Heading> */}
        <Text align={TEXT_ALIGN.CENTER}>
          Dags att skapa en bok? Du kan enkelt kombinera dina favoritavsnitt
          till en egen bok.
        </Text>
      </Popup>

      <Popup
        visible={openPopups.includes(POPUPS.TIP_COLLEAGUE)}
        // ctas={[{ label: 'Bibliotek' }, { label: 'Mitt lärarrum' }]}
        onCtaClick={onCtaClick}
        onCloseClick={onCloseClick}
      >
        {/* <Heading {...headingProps}>Välkommen till Doklet!</Heading> */}
        <Text align={TEXT_ALIGN.CENTER}>
          Tipsa gärna dina kollegor om Doklet!
        </Text>

        <div className={'flex justify-center mt-6'}>
          <Button
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            href={`mailto:?subject=${currentUserFullName} vill tipsa dig om ett nytt läromedel&body=Hej! %0D%0A %0D%0A ${currentUserFullName} vill tipsa dig om ett nytt läromedel i svenska och SO för åk 4-9. %0D%0A Det kanske kan vara intressant? %0D%0A %0D%0A Se https://www.doklet.se`}
            handleClick={() => {
              onCloseClick()
            }}
          >
            Tipsa en kollega
          </Button>
        </div>
      </Popup>

      <AnimatePresence>
        {openPopups.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Overlay
              handleClick={() => {
                // setOpenPopups([])
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Popups
